import { useState } from "react";
import type { LucideIcon } from "lucide-react";
import { FolderPlus, FolderOpen, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { usePermissions } from "@/hooks/usePermissions";
import { CreateProjectDialog } from "./CreateProjectDialog";

interface ProjectsEmptyStateProps {
  buildingId: string;
}

// One benefit tile in the feature row beneath the create card.
function FeatureCard({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start space-x-4">
          <div className="rounded-lg bg-primary/10 p-3">
            <Icon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h4 className="mb-2 font-semibold">{title}</h4>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Teaching-moment empty state for the folders page when a building has no
 * projects yet. Mirrors DocumentsEmptyState (centered call-to-action card plus
 * two feature cards) so the two empty pages feel like one product. The CTA
 * reuses CreateProjectDialog — same create mutation, toasts and query
 * invalidation as the project selector — and is gated on the create-projects
 * permission so subcontractors still get the explanation without a dead button.
 */
export function ProjectsEmptyState({ buildingId }: ProjectsEmptyStateProps) {
  const { t } = useLanguage();
  const { canCreateProjects } = usePermissions();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const e = t.projects.empty;

  return (
    <div className="space-y-8">
      {/* Create-your-first-project area */}
      <Card>
        <CardContent className="flex flex-col items-center justify-center p-12 text-center">
          <div className="mb-4 rounded-full bg-primary/10 p-4">
            <FolderPlus className="h-12 w-12 text-primary" />
          </div>
          <h3 className="mb-2 text-xl font-semibold">{e.title}</h3>
          <p className="mb-6 max-w-md text-muted-foreground">{e.description}</p>
          {canCreateProjects && (
            <Button onClick={() => setIsCreateOpen(true)} size="lg">
              <FolderPlus className="mr-2 h-5 w-5" />
              {e.createButton}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Feature overview */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FeatureCard icon={FolderOpen} title={e.organiseTitle} description={e.organiseDesc} />
        <FeatureCard icon={Users} title={e.collaborateTitle} description={e.collaborateDesc} />
      </div>

      <CreateProjectDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        buildingId={buildingId}
      />
    </div>
  );
}
